import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Typography Extensions
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import FontFamily from '@tiptap/extension-font-family';
import { FontSize } from './FontSizeExtension';

// 🟢 NEW CUSTOM EXTENSIONS (Replaces buggy Mathematics and handles Placeholders)
import { MathBlockExtension, MathInlineExtension } from './MathExtensions';
import AssetPlaceholder from './AssetPlaceholder';
import 'katex/dist/katex.min.css';

// UI Components
import { Button, ButtonGroup, Dropdown, Modal, Form, Spinner, Tooltip, OverlayTrigger, Tab, Nav } from 'react-bootstrap';
import { 
    Bold, Italic, Underline as UnderlineIcon, Strikethrough, 
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    List, ListOrdered, Image as ImageIcon, Sparkles,
    Download, FileText, Presentation, FileDown, Search, Wand2, Save,
    Undo, Redo, Table as TableIcon, Minus, Superscript as SuperIcon, Subscript as SubIcon,
    Highlighter, Palette
} from 'lucide-react';
import debounce from 'lodash/debounce';

import html2pdf from 'html2pdf.js';
import api from '../../api/api'; 
import { showToast } from '../../utils/toast';

const editorExtensions = [
    StarterKit.configure({ codeBlock: false }),
    Markdown.configure({ html: true, tightLists: true }),
    MathBlockExtension,
    MathInlineExtension,
    AssetPlaceholder,
    Image.configure({ HTMLAttributes: { class: 'img-fluid rounded shadow-sm my-4 d-block mx-auto' } }),
    Table.configure({ resizable: true }),
    TableRow, TableHeader, TableCell,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Underline,
    TextStyle,
    Color,
    FontFamily, 
    FontSize,   
    Highlight.configure({ multicolor: true }),
    Superscript,
    Subscript,
];

const TeachingMaterialEditor = ({ 
    initialContent, 
    tenantSlug, 
    projectId, 
    onSave, 
    documentTitle, 
    setDocumentTitle,
    // 🟢 ORCHESTRATION PROPS
    mode = 'document', 
    orchestrationData = { introduction: '', summary: '', conclusion: '' },
    setOrchestrationData = () => {}
}) => {
    // --- STATE ---
    const [isSaving, setIsSaving] = useState(false);
    const [showAiImageModal, setShowAiImageModal] = useState(false);
    const [aiImagePrompt, setAiImagePrompt] = useState('');
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [isExporting, setIsExporting] = useState(false);


    const [showUnsplashModal, setShowUnsplashModal] = useState(false);
    const [unsplashQuery, setUnsplashQuery] = useState('');
    const [unsplashResults, setUnsplashResults] = useState([]);
    const [isSearchingUnsplash, setIsSearchingUnsplash] = useState(false);

    const [showEditImageModal, setShowEditImageModal] = useState(false);
    const [editImagePrompt, setEditImagePrompt] = useState('');
    const [isEditingImage, setIsEditingImage] = useState(false);

    const fileInputRef = useRef(null);
    const [isUploadingLocal, setIsUploadingLocal] = useState(false);

    // --- 🟢 NEW: MARKDOWN CLEANER (Reverses TipTap HTML back to strict Markdown) ---
    const cleanMarkdown = (md) => {
        if (!md) return "";
        // Restore Math Blocks
        md = md.replace(/<div[^>]*data-type="math-block"[^>]*data-formula="([^"]+)"[^>]*>.*?<\/div>/gis, (match, encoded) => {
            return `\n$$\n${decodeURIComponent(encoded)}\n$$\n`;
        });
        // Restore Inline Math
        md = md.replace(/<span[^>]*data-type="math-inline"[^>]*data-formula="([^"]+)"[^>]*>.*?<\/span>/gis, (match, encoded) => {
            return `$${decodeURIComponent(encoded)}$`;
        });
        // Restore Asset Placeholders
        md = md.replace(/<div[^>]*data-type="asset-placeholder"[^>]*assettype="([^"]+)"[^>]*prompt="([^"]+)"[^>]*>.*?<\/div>/gis, (match, type, prompt) => {
            return `\n[${type}: ${prompt}]\n`;
        });
        return md;
    };

    const debouncedSave = useMemo(
        () => debounce((content) => {
            // 🛑 SAFETY GUARD: Prevent auto-saving an accidental complete wipe
            const cleanText = content ? content.trim() : "";
            
            if (cleanText.length < 50) {
                console.warn("Auto-save aborted: Content dropped below safety threshold. Please use manual save if this deletion was intentional.");
                // Optional: You could trigger a toast notification here alerting the user
                return;
            }

            if (onSave) onSave(content);
        }, 2000),
        [onSave]
    );

    useEffect(() => {
        return () => debouncedSave.cancel();
    }, [debouncedSave]);

    
    const handleManualSave = async () => {
        if (!editor || !onSave) return;
        setIsSaving(true);
        // Clean the markdown before saving so the DB stays pure!
        const markdown = cleanMarkdown(editor.storage.markdown.getMarkdown());
        try {
            await onSave(markdown);
        } finally {
            setIsSaving(false);
        }
    };

    const editor = useEditor({
        editable: true,
        extensions: editorExtensions,
        content: '', 
        onUpdate: ({ editor }) => {
            // Clean the markdown before auto-saving
            debouncedSave(cleanMarkdown(editor.storage.markdown.getMarkdown()));
        }
    });

    // --- 🟢 THE PRE-PROCESSOR (HYDRATION) ---
    useEffect(() => {
        if (editor && initialContent !== undefined) {
            let processedContent = initialContent;

            // 1. Convert [IMAGE: ...] placeholders into interactive NodeViews
            processedContent = processedContent.replace(
                /\[(IMAGE|GRAPH|DIAGRAM|TABLE|CODE):\s*(.*?)\]/gi, 
                '<div data-type="asset-placeholder" assettype="$1" prompt="$2"></div>'
            );

            // 2. Format $$ Block Math for Custom Extension
            processedContent = processedContent.replace(/\$\$(.*?)\$\$/gs, (match, formula) => {
                const encoded = encodeURIComponent(formula.trim());
                return `<div data-type="math-block" data-formula="${encoded}"></div>`;
            });

            // 3. Format $ Inline Math for Custom Extension
            processedContent = processedContent.replace(/\$(.*?)\$/g, (match, formula) => {
                const encoded = encodeURIComponent(formula.trim());
                return `<span data-type="math-inline" data-formula="${encoded}"></span>`;
            });

            setTimeout(() => {
                editor.commands.setContent(processedContent);
            }, 0);
        }
    }, [initialContent, editor]);

    // --- 🟢 EVENT LISTENER FOR ASSET PLACEHOLDERS ---
    useEffect(() => {
        const handleAssetAction = (e) => {
            const { actionType, prompt, pos } = e.detail;
            
            // Set the editor cursor exactly on the placeholder so that
            // when the image is inserted, it replaces the placeholder node!
            if (editor) {
                editor.commands.setNodeSelection(pos);
            }

            if (actionType === 'ai') {
                setAiImagePrompt(prompt);
                setShowAiImageModal(true);
            } else if (actionType === 'unsplash') {
                setUnsplashQuery(prompt);
                setShowUnsplashModal(true);
            } else if (actionType === 'upload') {
                if (fileInputRef.current) fileInputRef.current.click();
            }
        };

        window.addEventListener('trigger-asset-action', handleAssetAction);
        return () => window.removeEventListener('trigger-asset-action', handleAssetAction);
    }, [editor]);

    const base64ToFile = (dataURI, filename) => {
        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        return new File([blob], filename, { type: mimeString });
    };

    // --- EXPORT FUNCTIONS ---

    // Helper to build the final document
    const getFullExportContent = () => {
        let md = cleanMarkdown(editor.storage.markdown.getMarkdown());
        // 🟢 STITCH FULL ARCS STRUCTURE FOR ORCHESTRATION MODE
        if (mode === 'orchestration') {
            md = `# ${documentTitle || 'Unit Content'}\n\n## Introduction\n${orchestrationData.introduction || ''}\n\n## Lesson Content\n${md}\n\n## Summary\n${orchestrationData.summary || ''}\n\n## Keywords\n${orchestrationData.conclusion || ''}\n`;
        }
        return md;
    };

    const handleExportPdf = async () => {
        const fullMarkdown = getFullExportContent();
        if (!fullMarkdown) return;
        setIsExporting(true);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/export/pdf`, // 🟢 New Endpoint
                { content: fullMarkdown },
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob' 
                }
            );

            const file = new Blob([res.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute('download', `${(documentTitle || 'Teaching_Material').replace(/[^a-z0-9]/gi, '_')}.pdf`); 
            
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(fileURL);

        } catch (err) {
            console.error("PDF Export Error:", err);
            showToast("Failed to export PDF. Please check the console.", 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleBackendExport = async (format) => {
        const fullMarkdown = getFullExportContent();
        if (!fullMarkdown) return;

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/export/docx`,
                { content: fullMarkdown }, // Send stitched content
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob' 
                }
            );

            const file = new Blob([res.data], { 
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
            });

            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute('download', `${documentTitle.replace(/[^a-z0-9]/gi, '_') || 'Teaching_Material'}.docx`); 
            
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(fileURL);

        } catch (err) {
            console.error("DOCX Export Error:", err);
            showToast("Failed to export Word Document. Please check the console.", 'error');
        }
    };


    // --- ASSET HANDLERS ---
    const handleGenerateAiImage = async () => {
        if (!aiImagePrompt.trim() || !editor) return;
        setIsGeneratingImage(true);
        try {
            const token = localStorage.getItem('access_token');
            const genRes = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/generate-image`,
                { prompt: aiImagePrompt },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const imageFile = base64ToFile(genRes.data.image_url, `ai-gen-${Date.now()}.jpg`);
            const formData = new FormData();
            formData.append('file', imageFile);
            formData.append('placeholder_id', `ai_gen_${Date.now()}`);

            const uploadRes = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/upload-asset`, 
                formData, 
                { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
            );

            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8008";
            let s3Key = uploadRes.data.gcs_uri.replace(/^(\/)+/, "").replace(/^(university-econtent-engine-prod\/)+/, "");
            const absoluteProxyUrl = `${baseUrl}/university-econtent-engine-prod/${encodeURI(s3Key)}`;
            
            editor.chain().focus().setImage({ src: absoluteProxyUrl }).run();
            setShowAiImageModal(false);
            setAiImagePrompt('');
        } catch (err) {
            showToast("Failed to generate or upload image.", 'error');
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleLocalImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !editor) return;
        setIsUploadingLocal(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('placeholder_id', `tiptap_upload_${Date.now()}`);

        try {
            const token = localStorage.getItem('access_token');
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/upload-asset`, 
                formData, 
                { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
            );

            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8008";
            let s3Key = res.data.gcs_uri || "";
            s3Key = s3Key.replace(/^(\/)+/, "").replace(/^(university-econtent-engine-prod\/)+/, "");
            const absoluteProxyUrl = `${baseUrl}/university-econtent-engine-prod/${encodeURI(s3Key)}`;
            editor.chain().focus().setImage({ src: absoluteProxyUrl }).run();
        } catch (err) {
            showToast("Image upload failed.", 'error');
        } finally {
            setIsUploadingLocal(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSearchUnsplash = async (e) => {
        if (e) e.preventDefault();
        if (!unsplashQuery.trim()) return;
        setIsSearchingUnsplash(true);
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.get(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/unsplash-search?query=${encodeURIComponent(unsplashQuery)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUnsplashResults(res.data.results);
        } catch (err) {
            showToast("Failed to search Unsplash.", 'error');
        } finally {
            setIsSearchingUnsplash(false);
        }
    };

    const insertUnsplashImage = async (image) => {
        if (!editor) return;
        const contentToInsert = `\n![${image.alt_text || 'Educational image'}](${image.url_regular})\n\n<p style="text-align: center;"><small style="color: #6c757d;"><em>Photo by <a href="${image.author_link}?utm_source=AcademicOS&utm_medium=referral" target="_blank" rel="noopener noreferrer">${image.author_name}</a> on <a href="https://unsplash.com/?utm_source=AcademicOS&utm_medium=referral" target="_blank" rel="noopener noreferrer">Unsplash</a></em></small></p>\n\n`;
        editor.chain().focus().insertContent(contentToInsert).run();
            
        setShowUnsplashModal(false);
        setUnsplashQuery('');
        setUnsplashResults([]);

        try {
            const token = localStorage.getItem('access_token');
            await api.get(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/unsplash-track?download_url=${encodeURIComponent(image.download_location)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {}
    };

    const handleInstructEdit = async () => {
        if (!editor.isActive('image') || !editImagePrompt.trim()) return;
        const selectedImageSrc = editor.getAttributes('image').src;
        setIsEditingImage(true);
        
        try {
            const token = localStorage.getItem('access_token');
            const editRes = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/copilot/edit-image`,
                { prompt: editImagePrompt, base64_image: selectedImageSrc },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const imageFile = base64ToFile(editRes.data.image_url, `ai-edit-${Date.now()}.jpg`);
            const formData = new FormData();
            formData.append('file', imageFile);
            formData.append('placeholder_id', `ai_edit_${Date.now()}`);

            const uploadRes = await api.post(
                `/tenant/${tenantSlug}/projects/${projectId}/upload-asset`, 
                formData, 
                { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
            );

            const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8008";
            let s3Key = uploadRes.data.gcs_uri.replace(/^(\/)+/, "").replace(/^(university-econtent-engine-prod\/)+/, "");
            const absoluteProxyUrl = `${baseUrl}/university-econtent-engine-prod/${encodeURI(s3Key)}`;
            
            editor.chain().focus().setImage({ src: absoluteProxyUrl }).run();
            setShowEditImageModal(false);
            setEditImagePrompt('');
        } catch (err) {
            showToast("Failed to edit or upload image.", 'error');
        } finally {
            setIsEditingImage(false);
        }
    };

    if (!editor) return null;

    // --- ACCESSIBLE TOOLBAR ---
    const Toolbar = () => (
        <div className="bg-white border-bottom p-3 d-flex flex-wrap gap-3 align-items-center sticky-top shadow-sm" style={{ zIndex: 100 }}>
            <ButtonGroup>
                <Button variant="outline-secondary" size="md" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
                    <Undo size={20} />
                </Button>
                <Button variant="outline-secondary" size="md" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
                    <Redo size={20} />
                </Button>
            </ButtonGroup>

            <div className="vr d-none d-md-block mx-1 text-muted"></div>

            <ButtonGroup>
                <Button variant={editor.isActive('bold') ? 'primary' : 'outline-secondary'} size="md" onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
                    <Bold size={20} />
                </Button>
                <Button variant={editor.isActive('italic') ? 'primary' : 'outline-secondary'} size="md" onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
                    <Italic size={20} />
                </Button>
                <Button variant={editor.isActive('underline') ? 'primary' : 'outline-secondary'} size="md" onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
                    <UnderlineIcon size={20} />
                </Button>
                <Button variant={editor.isActive('strike') ? 'primary' : 'outline-secondary'} size="md" onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
                    <Strikethrough size={20} />
                </Button>
            </ButtonGroup>
            {/* 🟢 NEW: Font Family & Size Selectors */}
            <ButtonGroup className="align-items-center border rounded bg-light px-2 me-2">
                <Form.Select 
                    size="sm" 
                    className="border-0 bg-transparent text-secondary shadow-none"
                    style={{ width: '130px', cursor: 'pointer', fontWeight: '500' }}
                    onChange={(e) => {
                        if (e.target.value) {
                            editor.chain().focus().setFontFamily(e.target.value).run();
                        } else {
                            editor.chain().focus().unsetFontFamily().run();
                        }
                    }}
                    value={editor.getAttributes('textStyle').fontFamily || ''}
                    title="Font Family"
                >
                    <option value="">Default Font</option>
                    <option value="Arial">Arial</option>
                    <option value="Courier New">Courier</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Verdana">Verdana</option>
                </Form.Select>

                <div className="vr mx-1"></div>

                <Form.Select 
                    size="sm" 
                    className="border-0 bg-transparent text-secondary shadow-none"
                    style={{ width: '85px', cursor: 'pointer', fontWeight: '500' }}
                    onChange={(e) => {
                        if (e.target.value) {
                            editor.chain().focus().setFontSize(e.target.value).run();
                        } else {
                            editor.chain().focus().unsetFontSize().run();
                        }
                    }}
                    value={editor.getAttributes('textStyle').fontSize || ''}
                    title="Font Size"
                >
                    <option value="">Size</option>
                    <option value="12px">12px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="20px">20px</option>
                    <option value="24px">24px</option>
                    <option value="30px">30px</option>
                    <option value="36px">36px</option>
                </Form.Select>
            </ButtonGroup>

            <ButtonGroup className="align-items-center border rounded pe-1 bg-light">
                <Button variant={editor.isActive('subscript') ? 'secondary' : 'light'} className="border-0" onClick={() => editor.chain().focus().toggleSubscript().run()} title="Subscript">
                    <SubIcon size={18} />
                </Button>
                <Button variant={editor.isActive('superscript') ? 'secondary' : 'light'} className="border-0" onClick={() => editor.chain().focus().toggleSuperscript().run()} title="Superscript">
                    <SuperIcon size={18} />
                </Button>
                <div className="vr mx-1"></div>
                <OverlayTrigger overlay={<Tooltip>Highlight Color</Tooltip>} placement="top">
                    <div className="d-flex align-items-center px-2">
                        <Highlighter size={18} className="me-1 text-muted"/>
                        <input
                            type="color"
                            onInput={event => editor.chain().focus().toggleHighlight({ color: event.target.value }).run()}
                            value={editor.getAttributes('highlight').color || '#ffff00'}
                            className="form-control form-control-color p-0 border-0 bg-transparent"
                            style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                        />
                    </div>
                </OverlayTrigger>
                <div className="vr mx-1"></div>
                <OverlayTrigger overlay={<Tooltip>Text Color</Tooltip>} placement="top">
                    <div className="d-flex align-items-center px-2">
                        <Palette size={18} className="me-1 text-muted"/>
                        <input
                            type="color"
                            onInput={event => editor.chain().focus().setColor(event.target.value).run()}
                            value={editor.getAttributes('textStyle').color || '#000000'}
                            className="form-control form-control-color p-0 border-0 bg-transparent"
                            style={{ width: '25px', height: '25px', cursor: 'pointer' }}
                        />
                    </div>
                </OverlayTrigger>
            </ButtonGroup>

            <div className="vr d-none d-md-block mx-1 text-muted"></div>

            <ButtonGroup>
                <Button variant={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
                    <AlignLeft size={20} />
                </Button>
                <Button variant={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Center">
                    <AlignCenter size={20} />
                </Button>
                <Button variant={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
                    <AlignRight size={20} />
                </Button>
                <Button variant={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify">
                    <AlignJustify size={20} />
                </Button>
            </ButtonGroup>

            <div className="vr d-none d-md-block mx-1 text-muted"></div>

            <ButtonGroup>
                <Button variant={editor.isActive('bulletList') ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
                    <List size={20} />
                </Button>
                <Button variant={editor.isActive('orderedList') ? 'primary' : 'outline-secondary'} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
                    <ListOrdered size={20} />
                </Button>
                <Button variant="outline-secondary" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Insert Slide Break / Line">
                    <Minus size={20} />
                </Button>
            </ButtonGroup>

            <div className="vr d-none d-md-block mx-1 text-muted"></div>

            <div className="d-flex gap-2 flex-wrap ms-md-auto">
                <Button variant="outline-secondary" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert Table">
                    <TableIcon size={20} className="me-1" /> Table
                </Button>
                
                <input type="file" ref={fileInputRef} onChange={handleLocalImageUpload} accept="image/*" style={{ display: 'none' }} />
                <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()} disabled={isUploadingLocal} title="Upload Image from Computer">
                    {isUploadingLocal ? <Spinner size="sm" animation="border" aria-hidden="true" /> : <ImageIcon size={20} className="me-1" />} Upload
                </Button>

                <Button variant="outline-primary" onClick={() => setShowUnsplashModal(true)} title="Search Free Stock Photos">
                    <Search size={20} className="me-1" /> Human images
                </Button>
                    
                <Button variant="outline-success" onClick={() => setShowAiImageModal(true)} title="Generate AI Image">
                    <Sparkles size={20} className="me-1" /> Gen AI
                </Button>

                {editor.isActive('image') && (
                    <Button variant="info" className="text-white" onClick={() => setShowEditImageModal(true)} title="Edit Selected Image">
                        <Wand2 size={20} className="me-1" /> Edit Image
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <div className="d-flex flex-column h-100 bg-light border rounded shadow-lg overflow-hidden">
            
            <div className="p-3 bg-white border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center flex-grow-1 ps-2">
                    <FileText className="text-primary me-3" size={32} />
                    <input 
                        type="text" 
                        className="form-control border-0 fw-bold text-dark bg-transparent p-0 shadow-none" 
                        value={documentTitle} 
                        onChange={(e) => setDocumentTitle(e.target.value)} 
                        placeholder="Untitled Document"
                        style={{ fontSize: '1.6rem', width: '100%', maxWidth: '600px' }}
                        title="Click to rename document"
                    />
                </div>
                
                <div className="d-flex align-items-center gap-3 pe-2">
                    <Button variant="primary" size="lg" onClick={handleManualSave} disabled={isSaving} className="d-flex align-items-center px-4 shadow-sm">
                        {isSaving ? <Spinner size="sm" animation="border" className="me-2" aria-hidden="true" /> : <Save size={22} className="me-2" />}
                        Save Draft
                    </Button>

                    <Dropdown>
                        <Dropdown.Toggle variant="outline-dark" size="lg" id="dropdown-export" disabled={isExporting} className="d-flex align-items-center shadow-sm">
                            {isExporting ? <Spinner size="sm" animation="border" className="me-2" aria-hidden="true" /> : <Download size={22} className="me-2" />}
                            Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="shadow-lg p-2" style={{ minWidth: '250px' }}>
                            <Dropdown.Item onClick={handleExportPdf} className="py-3 fw-bold border-bottom">
                                <FileText size={20} className="me-3 text-danger" /> Export to PDF
                            </Dropdown.Item>
                            
                            <Dropdown.Item
                                onClick={() => handleBackendExport('docx')}
                                className={`py-3 fw-bold ${mode !== 'orchestration' ? 'border-bottom' : ''}`}
                            >
                                <FileDown size={20} className="me-3 text-primary" /> Export to Word (DOCX)
                            </Dropdown.Item>

                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <Toolbar />
            
            {/* 🟢 DUAL-TAB ORCHESTRATION RENDERER */}
            <div className="flex-grow-1 overflow-auto p-4 custom-scrollbar bg-light">
                <style>{`
                    .ProseMirror { outline: none !important; min-height: 500px; }
                    .tiptap { line-height: 1.85; font-size: 1.2rem; color: #1a1a1a; font-family: system-ui, -apple-system, sans-serif; }
                    .tiptap p { margin-bottom: 1.5rem; }
                    .tiptap h1 { font-size: 2.2rem; color: #004085; margin-top: 2.5rem; margin-bottom: 1.2rem; border-bottom: 2px solid #e9ecef; padding-bottom: 0.5rem; }
                    .tiptap h2 { font-size: 1.8rem; color: #0056b3; margin-top: 2rem; margin-bottom: 1rem; }
                    .tiptap h3 { font-size: 1.5rem; color: #007bff; margin-top: 1.5rem; }
                    
                    .tiptap hr { border: none; border-top: 3px solid #dee2e6; margin: 3rem 0; border-radius: 5px; }
                    .tiptap mark { background-color: #ffe066; border-radius: 2px; padding: 0.1em 0.2em; }
                    
                    .tiptap table { border-collapse: collapse; margin: 2rem 0; width: 100%; border: 1px solid #ced4da; }
                    .tiptap td, .tiptap th { border: 1px solid #ced4da; padding: 12px; min-width: 1em; position: relative; }
                    .tiptap th { background-color: #e9ecef; font-weight: bold; text-align: left; }
                    .tiptap .selectedCell:after { z-index: 2; position: absolute; content: ""; left: 0; right: 0; top: 0; bottom: 0; background: rgba(0, 123, 255, 0.15); pointer-events: none; }

                    .tiptap img { max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); margin: 2rem auto; display: block; }
                    .tiptap img.ProseMirror-selectednode { outline: 4px solid #007bff; outline-offset: 2px; }
                `}</style>

                {mode === 'orchestration' ? (
                    <Tab.Container defaultActiveKey="content">
                        <Nav variant="tabs" className="mb-4 justify-content-center border-0">
                            <Nav.Item>
                                <Nav.Link eventKey="content" className="fw-bold px-4 rounded-top">
                                    📖 Main Lesson Content
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="metadata" className="fw-bold px-4 rounded-top">
                                    ⚙️ Orchestration Metadata
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        
                        <Tab.Content>
                            <Tab.Pane eventKey="content">
                                <div className="bg-white mx-auto shadow-sm p-4 p-md-5 tiptap-sandbox-container border rounded" style={{ maxWidth: '1200px', minHeight: '850px' }}>
                                    <EditorContent editor={editor} />
                                </div>
                            </Tab.Pane>
                            
                            <Tab.Pane eventKey="metadata">
                                <div className="bg-white mx-auto shadow-sm p-4 p-md-5 border rounded" style={{ maxWidth: '900px' }}>
                                    <h4 className="fw-bold text-primary mb-4">ARCS Structure Metadata</h4>
                                    
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold text-muted text-uppercase small">Introduction</Form.Label>
                                        <Form.Control 
                                            as="textarea" rows={3} 
                                            value={orchestrationData?.introduction || ''} 
                                            onChange={(e) => setOrchestrationData({...orchestrationData, introduction: e.target.value})}
                                            placeholder="Pedagogical introduction..."
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold text-muted text-uppercase small">Unit Summary</Form.Label>
                                        <Form.Control 
                                            as="textarea" rows={6}
                                            value={orchestrationData?.summary || ''} 
                                            onChange={(e) => setOrchestrationData({...orchestrationData, summary: e.target.value})}
                                            placeholder="Bulleted summary points..."
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Label className="fw-bold text-muted text-uppercase small">Academic Keywords</Form.Label>
                                        <Form.Control 
                                            as="textarea" rows={4}
                                            value={orchestrationData?.conclusion || ''} 
                                            onChange={(e) => setOrchestrationData({...orchestrationData, conclusion: e.target.value})}
                                            placeholder="Keywords for the unit index..."
                                        />
                                    </Form.Group>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                ) : (
                    <div className="bg-white mx-auto shadow-sm p-4 p-md-5 tiptap-sandbox-container border rounded" style={{ maxWidth: '1200px', minHeight: '850px' }}>
                        <EditorContent editor={editor} />
                    </div>
                )}
            </div>

            {/* AI Image Generation Modal */}
            <Modal show={showAiImageModal} onHide={() => setShowAiImageModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2 fw-bold">
                        <Sparkles className="text-success" size={28} /> Generate AI Diagram
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form.Group>
                        <Form.Label className="fw-bold fs-5">Describe the instructional image you need</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={4}
                            className="fs-5"
                            placeholder="e.g., A highly detailed diagram of a simple closed-loop thermodynamic system with labels..." 
                            value={aiImagePrompt} 
                            onChange={(e) => setAiImagePrompt(e.target.value)} 
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="lg" onClick={() => setShowAiImageModal(false)}>Cancel</Button>
                    <Button variant="success" size="lg" onClick={handleGenerateAiImage} disabled={!aiImagePrompt.trim() || isGeneratingImage}>
                        {isGeneratingImage ? <><Spinner size="sm" className="me-2" aria-hidden="true" /> Generating AI Image...</> : 'Generate & Insert'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* UNSPLASH MODAL */}
            <Modal show={showUnsplashModal} onHide={() => setShowUnsplashModal(false)} size="xl" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2 fw-bold">
                        <ImageIcon className="text-primary" size={28} /> Search Free Educational Photos
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form onSubmit={handleSearchUnsplash} className="d-flex gap-3 mb-4">
                        <Form.Control 
                            type="text" 
                            size="lg"
                            placeholder="Search high-resolution images (e.g., 'laboratory', 'classroom', 'bridge')..." 
                            value={unsplashQuery}
                            onChange={(e) => setUnsplashQuery(e.target.value)}
                            autoFocus
                        />
                        <Button type="submit" variant="primary" size="lg" disabled={isSearchingUnsplash || !unsplashQuery.trim()} className="px-5 fw-bold">
                            {isSearchingUnsplash ? <Spinner size="sm" animation="border" aria-hidden="true" /> : 'Search'}
                        </Button>
                    </Form>

                    {unsplashResults.length > 0 ? (
                        <div className="d-flex flex-wrap gap-3 overflow-auto custom-scrollbar" style={{ maxHeight: '550px' }}>
                            {unsplashResults.map(img => (
                                <div 
                                    key={img.id} 
                                    className="position-relative border rounded shadow-sm overflow-hidden bg-dark"
                                    style={{ width: 'calc(33.333% - 11px)', cursor: 'pointer', height: '220px', transition: 'transform 0.2s' }}
                                    onClick={() => insertUnsplashImage(img)}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                    <img src={img.url_thumb} alt={img.alt_text} className="w-100 h-100 opacity-75" style={{ objectFit: 'cover' }} />
                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white opacity-0" 
                                         style={{ background: 'rgba(0,0,0,0.5)', transition: 'opacity 0.2s' }}
                                         onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                         onMouseOut={(e) => e.currentTarget.style.opacity = '0'}
                                    >
                                        <div className="text-center">
                                            <Sparkles size={32} className="mb-2"/>
                                            <h5 className="fw-bold m-0">Click to Insert</h5>
                                        </div>
                                    </div>
                                    <div className="position-absolute bottom-0 start-0 w-100 p-2 text-white text-truncate" 
                                         style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))', fontSize: '0.9rem' }}>
                                        &#128247; {img.author_name}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        !isSearchingUnsplash && unsplashQuery && (
                            <div className="text-center text-muted py-5 fs-5">
                                No images found for "{unsplashQuery}".
                            </div>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="w-100 text-center text-muted">
                        Photos provided by <a href="https://unsplash.com/?utm_source=AcademicOS&utm_medium=referral" target="_blank" rel="noreferrer" className="fw-bold text-decoration-none">Unsplash</a>
                    </div>
                </Modal.Footer>
            </Modal>


            {/* Instruct Image Edit Modal */}
            <Modal show={showEditImageModal} onHide={() => setShowEditImageModal(false)} centered size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2 fw-bold">
                        <Wand2 className="text-info" size={28} /> AI Image Editor
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form.Group>
                        <Form.Label className="fw-bold fs-5">What would you like to change in this image?</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={4}
                            className="fs-5"
                            placeholder="e.g., 'Correct the spelling from Termodynamics to Thermodynamics', 'Add a blue arrow pointing to the combustion chamber'..." 
                            value={editImagePrompt} 
                            onChange={(e) => setEditImagePrompt(e.target.value)} 
                            autoFocus
                        />
                        <Form.Text className="text-muted mt-2 d-block fs-6">
                            Give a direct instruction. The AI will modify the selected image based on your command while preserving the rest of the content.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="lg" onClick={() => setShowEditImageModal(false)}>Cancel</Button>
                    <Button variant="info" size="lg" className="text-white fw-bold" onClick={handleInstructEdit} disabled={!editImagePrompt.trim() || isEditingImage}>
                        {isEditingImage ? <><Spinner size="sm" className="me-2" aria-hidden="true" /> Processing Edit...</> : 'Apply AI Edit'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TeachingMaterialEditor;